import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { MediaPicker } from "@/components/MediaPicker";
import { Loader2, Plus, X, Save, MapPin, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const KIND_LABEL = { llm_text: "Texto", llm_image: "Imágenes", embedding: "Embeddings", publish: "Publicaciones", metrics_read: "Métricas de red" };
const formatUsd = (n) => (n > 0 && n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);

function UsageCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/usage/summary")
      .then((r) => setUsage(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !usage) return null;

  const percent = usage.percent_used;
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4" data-testid="usage-card">
      <div>
        <h3 className="font-heading font-bold text-[#09090B]">Uso y costo (estimado)</h3>
        <p className="text-xs text-[#71717A] mt-1 max-w-md">
          Costo estimado del mes en curso a partir del consumo de IA. No es la factura real de cada
          proveedor -- las tarifas pueden cambiar y no se pueden verificar desde acá.
        </p>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-heading font-bold text-[#09090B]" data-testid="usage-total-cost">
          {formatUsd(usage.total_cost_usd)}
        </span>
        <span className="text-xs text-[#71717A]">estimado este mes</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.keys(usage.breakdown).length === 0 ? (
          <span className="text-xs text-[#A1A1AA]" data-testid="usage-no-consumption">
            Sin consumo registrado todavía este mes.
          </span>
        ) : (
          Object.entries(usage.breakdown).map(([kind, cost]) => (
            <span
              key={kind} data-testid={`usage-breakdown-${kind}`}
              className="text-xs px-2 py-1 rounded-sm bg-[#F4F4F5] text-[#3F3F46]"
            >
              {KIND_LABEL[kind] || kind}: {formatUsd(cost)}
            </span>
          ))
        )}
      </div>
      {usage.unknown_cost_events && usage.unknown_cost_events.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="usage-unknown-cost">
          {usage.unknown_cost_events.map((e) => (
            <span
              key={`${e.kind}-${e.provider}`}
              className="text-xs px-2 py-1 rounded-sm bg-[#FEF3C7] text-[#92400E]"
              title="Esta red cobra por leer su API, pero no publica una tarifa por consulta -- no se inventa un monto."
            >
              + {e.units} consulta{e.units === 1 ? "" : "s"} a {e.provider} sin precio conocido
            </span>
          ))}
        </div>
      )}
      {usage.monthly_cost_cap_usd ? (
        <div className="text-xs text-[#71717A]" data-testid="usage-cap">
          Tope mensual: ${usage.monthly_cost_cap_usd.toFixed(2)} — llevás usado el {percent}%
          {percent >= 100 && (
            <span className="text-[#EF4444] font-medium"> (tope alcanzado, la generación está bloqueada)</span>
          )}
        </div>
      ) : (
        <div className="text-xs text-[#A1A1AA]" data-testid="usage-no-cap">Sin tope de consumo configurado.</div>
      )}
      {usage.daily.length > 0 && (
        <div className="h-48" data-testid="usage-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usage.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Area type="monotone" dataKey="cost_usd" stroke="#0055FF" fill="#0055FF" fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

const WRITE_ROLES = ["admin", "manager"];
const csv = (arr) => (arr || []).join(", ");
const parseCsv = (s) => (Array.isArray(s) ? s : s.split(",")).map((x) => (typeof x === "string" ? x.trim() : x)).filter(Boolean);

const EMPTY_ADDRESS = { label: "", street: "", city: "", state: "", zip_code: "", is_primary: false };
const EMPTY_CERT = { name: "", issuer: "", credential_id: "", year: "" };
const EMPTY_FAQ = { question: "", answer: "" };
const EMPTY_CITY = { city: "", county: "", zip_codes: "", priority: 3 };
const EMPTY_AREA = { state: "", region: "", cities: [] };

export default function CompanyPage() {
  const { role, activeTenantId } = useAuth();
  const canWrite = WRITE_ROLES.includes(role);
  const isAdmin = role === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);
  const [savingAutoPublish, setSavingAutoPublish] = useState(false);

  const emptyForm = {
    legal_name: "", trade_name: "", description: "", hours: "", years_in_business: "",
    phones_csv: "", emails_csv: "", websites_csv: "", services_csv: "", brand_tone: "", logo_media_id: null,
    categories_csv: "", founded_year: "", history: "", blog_cta_text: "Ready to get started? Request a free quote.",
    sms_link_domain: "",
    addresses: [], certifications: [], faqs: [], service_areas: [],
  };

  useEffect(() => {
    if (!activeTenantId) return;
    setLoading(true);
    (async () => {
      try {
        const [companyRes, tenantRes] = await Promise.all([api.get("/company"), api.get("/tenant")]);
        const c = companyRes.data;
        setAutoPublish(!!tenantRes.data?.auto_publish_approved);
        setForm(c ? {
          legal_name: c.legal_name || "", trade_name: c.trade_name || "",
          description: c.description || "", hours: c.hours || "",
          years_in_business: c.years_in_business ?? "",
          phones_csv: csv((c.phones || []).map((p) => p.number)),
          emails_csv: csv(c.emails), websites_csv: csv(c.websites),
          services_csv: csv(c.services), brand_tone: c.brand_tone || "", logo_media_id: c.logo_media_id || null,
          categories_csv: csv(c.categories), founded_year: c.founded_year ?? "", history: c.history || "",
          blog_cta_text: c.blog_cta_text || "Ready to get started? Request a free quote.",
          sms_link_domain: c.sms_link_domain || "",
          addresses: c.addresses || [],
          certifications: (c.certifications || []).map((x) => ({ ...x, year: x.year ?? "" })),
          faqs: c.faqs || [],
          service_areas: (c.service_areas || []).map((a) => ({
            ...a, cities: (a.cities || []).map((city) => ({ ...city, zip_codes: csv(city.zip_codes) })),
          })),
        } : { ...emptyForm });
      } catch (e) {
        setForm({ ...emptyForm });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addRow = (key, empty) => setForm((f) => ({ ...f, [key]: [...f[key], { ...empty }] }));
  const removeRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));
  const setRowField = (key, i, field, v) =>
    setForm((f) => ({ ...f, [key]: f[key].map((row, idx) => (idx === i ? { ...row, [field]: v } : row)) }));

  const addArea = () => setForm((f) => ({ ...f, service_areas: [...f.service_areas, { ...EMPTY_AREA, cities: [] }] }));
  const removeArea = (i) => setForm((f) => ({ ...f, service_areas: f.service_areas.filter((_, idx) => idx !== i) }));
  const setAreaField = (i, field, v) =>
    setForm((f) => ({ ...f, service_areas: f.service_areas.map((a, idx) => (idx === i ? { ...a, [field]: v } : a)) }));
  const addCity = (areaIdx) =>
    setForm((f) => ({
      ...f, service_areas: f.service_areas.map((a, idx) => (idx === areaIdx ? { ...a, cities: [...a.cities, { ...EMPTY_CITY }] } : a)),
    }));
  const removeCity = (areaIdx, cityIdx) =>
    setForm((f) => ({
      ...f,
      service_areas: f.service_areas.map((a, idx) => (idx === areaIdx ? { ...a, cities: a.cities.filter((_, ci) => ci !== cityIdx) } : a)),
    }));
  const setCityField = (areaIdx, cityIdx, field, v) =>
    setForm((f) => ({
      ...f,
      service_areas: f.service_areas.map((a, idx) =>
        idx === areaIdx ? { ...a, cities: a.cities.map((c, ci) => (ci === cityIdx ? { ...c, [field]: v } : c)) } : a
      ),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        legal_name: form.legal_name, trade_name: form.trade_name || null,
        description: form.description || null, hours: form.hours || null,
        years_in_business: form.years_in_business ? parseInt(form.years_in_business, 10) : null,
        phones: parseCsv(form.phones_csv).map((number) => ({ label: "principal", number })),
        emails: parseCsv(form.emails_csv), websites: parseCsv(form.websites_csv),
        services: parseCsv(form.services_csv), brand_tone: form.brand_tone || null, logo_media_id: form.logo_media_id,
        categories: parseCsv(form.categories_csv),
        founded_year: form.founded_year ? parseInt(form.founded_year, 10) : null,
        history: form.history || null,
        blog_cta_text: form.blog_cta_text || "Ready to get started? Request a free quote.",
        sms_link_domain: form.sms_link_domain?.trim() || null,
        addresses: form.addresses.filter((a) => a.label && a.street),
        certifications: form.certifications.filter((c) => c.name).map((c) => ({
          ...c, year: c.year ? parseInt(c.year, 10) : null,
        })),
        faqs: form.faqs.filter((f) => f.question && f.answer),
        service_areas: form.service_areas.filter((a) => a.state).map((a) => ({
          state: a.state, region: a.region || null,
          cities: (a.cities || []).filter((c) => c.city).map((c) => ({
            city: c.city, county: c.county || null,
            zip_codes: parseCsv(c.zip_codes), priority: parseInt(c.priority, 10) || 3,
          })),
        })),
      };
      await api.put("/company", payload);
      toast.success("Compañía guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoPublish = async (value) => {
    setSavingAutoPublish(true);
    try {
      await api.put("/tenant/settings/auto-publish", { auto_publish_approved: value });
      setAutoPublish(value);
      toast.success(value ? "El contenido aprobado se programa solo" : "El contenido aprobado requiere confirmación manual");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el switch");
    } finally {
      setSavingAutoPublish(false);
    }
  };

  if (loading || !form)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="company-page">
      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-5">
        <h3 className="font-heading font-bold text-[#09090B]">Identidad</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Nombre legal"><Input data-testid="company-legal-name" disabled={!canWrite} value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} /></Field>
          <Field label="Nombre comercial"><Input data-testid="company-trade-name" disabled={!canWrite} value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)} /></Field>
          <Field label="Años en el rubro"><Input data-testid="company-years" type="number" disabled={!canWrite} value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} /></Field>
          <Field label="Año de fundación"><Input data-testid="company-founded-year" type="number" disabled={!canWrite} value={form.founded_year} onChange={(e) => set("founded_year", e.target.value)} placeholder="2013" /></Field>
          <Field label="Horarios"><Input data-testid="company-hours" disabled={!canWrite} value={form.hours} onChange={(e) => set("hours", e.target.value)} /></Field>
          <Field label="Categorías (tipo Google Business, separadas por coma)"><Input data-testid="company-categories" disabled={!canWrite} value={form.categories_csv} onChange={(e) => set("categories_csv", e.target.value)} placeholder="Insulation Contractor, Spray Foam Insulation" /></Field>
        </div>
        <Field label="Descripción"><Textarea data-testid="company-description" disabled={!canWrite} value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} /></Field>
        <Field label="Historia de la empresa (cuándo y cómo se formó)"><Textarea data-testid="company-history" disabled={!canWrite} value={form.history} onChange={(e) => set("history", e.target.value)} rows={3} /></Field>
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-5">
        <h3 className="font-heading font-bold text-[#09090B]">Contacto y servicios</h3>
        <Field label="Teléfonos (separados por coma)"><Input data-testid="company-phones" disabled={!canWrite} value={form.phones_csv} onChange={(e) => set("phones_csv", e.target.value)} /></Field>
        <Field label="Emails (separados por coma)"><Input data-testid="company-emails" disabled={!canWrite} value={form.emails_csv} onChange={(e) => set("emails_csv", e.target.value)} /></Field>
        <Field label="Sitios web (separados por coma)"><Input data-testid="company-websites" disabled={!canWrite} value={form.websites_csv} onChange={(e) => set("websites_csv", e.target.value)} /></Field>
        <Field label="Servicios (separados por coma)"><Input data-testid="company-services" disabled={!canWrite} value={form.services_csv} onChange={(e) => set("services_csv", e.target.value)} /></Field>
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#09090B]">Direcciones</h3>
          {canWrite && <Button data-testid="add-address-button" variant="outline" size="sm" onClick={() => addRow("addresses", EMPTY_ADDRESS)} className="rounded-md"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>}
        </div>
        {form.addresses.length === 0 && <p className="text-sm text-[#71717A]">Sin direcciones cargadas.</p>}
        {form.addresses.map((a, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr_70px_90px_40px] gap-3 items-end" data-testid={`address-row-${i}`}>
            <Field label="Etiqueta"><Input disabled={!canWrite} value={a.label} onChange={(e) => setRowField("addresses", i, "label", e.target.value)} placeholder="Oficina Brooklyn" /></Field>
            <Field label="Calle"><Input disabled={!canWrite} value={a.street} onChange={(e) => setRowField("addresses", i, "street", e.target.value)} /></Field>
            <Field label="Ciudad"><Input disabled={!canWrite} value={a.city} onChange={(e) => setRowField("addresses", i, "city", e.target.value)} /></Field>
            <Field label="Estado"><Input disabled={!canWrite} value={a.state} onChange={(e) => setRowField("addresses", i, "state", e.target.value)} maxLength={2} /></Field>
            <Field label="ZIP"><Input disabled={!canWrite} value={a.zip_code} onChange={(e) => setRowField("addresses", i, "zip_code", e.target.value)} /></Field>
            {canWrite && <Button variant="ghost" size="icon" onClick={() => removeRow("addresses", i)} className="text-[#EF4444]" data-testid={`remove-address-${i}`}><X className="h-4 w-4" /></Button>}
            <label className="flex items-center gap-2 text-xs text-[#71717A] md:col-span-6">
              <Checkbox disabled={!canWrite} checked={a.is_primary} onCheckedChange={(v) => setRowField("addresses", i, "is_primary", !!v)} /> Oficina principal
            </label>
          </div>
        ))}
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#09090B]">Certificaciones</h3>
          {canWrite && <Button data-testid="add-certification-button" variant="outline" size="sm" onClick={() => addRow("certifications", EMPTY_CERT)} className="rounded-md"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>}
        </div>
        {form.certifications.length === 0 && <p className="text-sm text-[#71717A]">Sin certificaciones cargadas.</p>}
        {form.certifications.map((c, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr_1fr_90px_40px] gap-3 items-end" data-testid={`certification-row-${i}`}>
            <Field label="Nombre"><Input disabled={!canWrite} value={c.name} onChange={(e) => setRowField("certifications", i, "name", e.target.value)} placeholder="Certified Spray Foam Installer" /></Field>
            <Field label="Emisor"><Input disabled={!canWrite} value={c.issuer || ""} onChange={(e) => setRowField("certifications", i, "issuer", e.target.value)} placeholder="SPFA" /></Field>
            <Field label="ID de credencial"><Input disabled={!canWrite} value={c.credential_id || ""} onChange={(e) => setRowField("certifications", i, "credential_id", e.target.value)} /></Field>
            <Field label="Año"><Input type="number" disabled={!canWrite} value={c.year} onChange={(e) => setRowField("certifications", i, "year", e.target.value)} /></Field>
            {canWrite && <Button variant="ghost" size="icon" onClick={() => removeRow("certifications", i)} className="text-[#EF4444]" data-testid={`remove-certification-${i}`}><X className="h-4 w-4" /></Button>}
          </div>
        ))}
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#09090B]">Preguntas frecuentes</h3>
          {canWrite && <Button data-testid="add-faq-button" variant="outline" size="sm" onClick={() => addRow("faqs", EMPTY_FAQ)} className="rounded-md"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>}
        </div>
        {form.faqs.length === 0 && <p className="text-sm text-[#71717A]">Sin preguntas cargadas.</p>}
        {form.faqs.map((f, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_40px] gap-3" data-testid={`faq-row-${i}`}>
            <div className="space-y-2">
              <Input disabled={!canWrite} value={f.question} onChange={(e) => setRowField("faqs", i, "question", e.target.value)} placeholder="Pregunta" />
              <Textarea disabled={!canWrite} value={f.answer} onChange={(e) => setRowField("faqs", i, "answer", e.target.value)} placeholder="Respuesta" rows={2} />
            </div>
            {canWrite && <Button variant="ghost" size="icon" onClick={() => removeRow("faqs", i)} className="text-[#EF4444]" data-testid={`remove-faq-${i}`}><X className="h-4 w-4" /></Button>}
          </div>
        ))}
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4">
        <h3 className="font-heading font-bold text-[#09090B]">Marca (usado en contenido con IA e imágenes)</h3>
        <Field label="Tono de marca">
          <Textarea data-testid="company-brand-tone" disabled={!canWrite} value={form.brand_tone} onChange={(e) => set("brand_tone", e.target.value)} rows={2} placeholder="profesional, cercano y sin tecnicismos innecesarios" />
        </Field>
        <Field label="CTA al final de los blogs publicados en WordPress">
          <Input data-testid="company-blog-cta-text" disabled={!canWrite} value={form.blog_cta_text} onChange={(e) => set("blog_cta_text", e.target.value)} placeholder="Ready to get started? Request a free quote." />
        </Field>
        <Field label="Dominio del link de SMS">
          <Input data-testid="company-sms-link-domain" disabled={!canWrite} value={form.sms_link_domain} onChange={(e) => set("sms_link_domain", e.target.value)} placeholder="guias.tudominio.com" />
        </Field>
        {!form.sms_link_domain?.trim() && (
          <p className="text-xs text-[#F59E0B] flex items-start gap-1.5" data-testid="sms-link-domain-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Sin un subdominio propio (con un CNAME hacia nosotros), los links de SMS van a salir con
            nuestro dominio y la campaña A2P puede ser rechazada por Twilio ("third-party URLs").
          </p>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#71717A]">Logo (para el watermark de imágenes generadas)</Label>
          {canWrite && (
            <Button variant="outline" size="sm" onClick={() => setLogoPickerOpen((v) => !v)} data-testid="toggle-logo-picker-button" className="rounded-md">
              {form.logo_media_id ? "Cambiar logo" : "Elegir logo"}
            </Button>
          )}
          {logoPickerOpen && <MediaPicker selected={form.logo_media_id} onSelect={(id) => { set("logo_media_id", id); setLogoPickerOpen(false); }} />}
        </div>
      </Card>

      {isAdmin && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-[#09090B]">Flujo de contenido</h3>
              <p className="text-xs text-[#71717A] mt-1 max-w-md">
                Si está activo, el contenido que pasa los 4 chequeos de revisión sin errores se aprueba solo.
                Si está apagado, siempre requiere que una persona lo apruebe manualmente.
              </p>
            </div>
            <Switch checked={autoPublish} onCheckedChange={toggleAutoPublish} disabled={savingAutoPublish} data-testid="auto-publish-switch" />
          </div>
        </Card>
      )}

      {isAdmin && <UsageCard />}

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#0055FF]" />
            <h3 className="font-heading font-bold text-[#09090B]">Áreas de servicio</h3>
          </div>
          {canWrite && <Button data-testid="add-area-button" variant="outline" size="sm" onClick={addArea} className="rounded-md"><Plus className="h-4 w-4 mr-1" /> Agregar estado</Button>}
        </div>
        <p className="text-xs text-[#71717A]">Cada estado agrupa sus ciudades con condado, códigos postales y prioridad (1-5, usada por el planificador de campañas para decidir qué contenido generar primero).</p>
        {form.service_areas.length === 0 && <p className="text-sm text-[#71717A]">Sin áreas cargadas.</p>}
        <Accordion type="multiple" className="w-full" defaultValue={form.service_areas.map((_, i) => `area-${i}`)}>
          {form.service_areas.map((a, i) => (
            <AccordionItem key={i} value={`area-${i}`} data-testid={`area-row-${i}`}>
              <AccordionTrigger data-testid={`area-trigger-${i}`}>
                {a.state || "(sin estado)"}{a.region ? ` — ${a.region}` : ""}
                <span className="text-[#A1A1AA] ml-2 text-xs">{(a.cities || []).length} ciudad(es)</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-[100px_1fr_40px] gap-3 items-end">
                  <Field label="Estado"><Input disabled={!canWrite} value={a.state} onChange={(e) => setAreaField(i, "state", e.target.value)} placeholder="NY" maxLength={2} /></Field>
                  <Field label="Región (opcional)"><Input disabled={!canWrite} value={a.region || ""} onChange={(e) => setAreaField(i, "region", e.target.value)} placeholder="Long Island" /></Field>
                  {canWrite && <Button variant="ghost" size="icon" onClick={() => removeArea(i)} className="text-[#EF4444]" data-testid={`remove-area-${i}`}><X className="h-4 w-4" /></Button>}
                </div>
                {(a.cities || []).map((c, ci) => (
                  <div key={ci} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_70px_40px] gap-3 items-end pl-4 border-l-2 border-[#E4E4E7]" data-testid={`city-row-${i}-${ci}`}>
                    <Field label="Ciudad"><Input disabled={!canWrite} value={c.city} onChange={(e) => setCityField(i, ci, "city", e.target.value)} /></Field>
                    <Field label="Condado (opcional)"><Input disabled={!canWrite} value={c.county || ""} onChange={(e) => setCityField(i, ci, "county", e.target.value)} placeholder="Nassau County" /></Field>
                    <Field label="Códigos postales"><Input disabled={!canWrite} value={c.zip_codes} onChange={(e) => setCityField(i, ci, "zip_codes", e.target.value)} /></Field>
                    <Field label="Prioridad">
                      <Input type="number" min={1} max={5} disabled={!canWrite} value={c.priority} onChange={(e) => setCityField(i, ci, "priority", e.target.value)} />
                    </Field>
                    {canWrite && <Button variant="ghost" size="icon" onClick={() => removeCity(i, ci)} className="text-[#EF4444]" data-testid={`remove-city-${i}-${ci}`}><X className="h-4 w-4" /></Button>}
                  </div>
                ))}
                {canWrite && (
                  <Button variant="outline" size="sm" onClick={() => addCity(i)} className="rounded-md" data-testid={`add-city-button-${i}`}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar ciudad
                  </Button>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>

      {canWrite && (
        <div className="flex justify-end">
          <Button data-testid="save-company-button" onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md active:scale-[0.98] transition-colors">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar cambios
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-[#71717A]">{label}</Label>
      {children}
    </div>
  );
}
